// Coming back to the page where you left it. iOS discards a backgrounded tab
// to save memory, and switching back reloads it; the browser's own scroll
// restoration runs before the board has rendered, so the reload lands at the
// top. This remembers the scroll position per tab (sessionStorage, which a
// reload keeps and a new tab doesn't) and goes back there once the page is
// tall enough. It only restores on page load: moving between pages in the
// app is untouched.

import { FEATURE_FLAGS, FLAG_DEFAULTS, getFeatureFlag } from './featureFlags';
import { isBraveIOS, isGoogleAppIOS, isIOS } from './platform';

const KEY = 'scrollPosition';
/** How long the page may take to grow tall enough. */
const RESTORE_WINDOW_MS = 5000;
const RETRY_MS = 50;
const SAVE_DELAY_MS = 100;
/** Any of these means the user is scrolling themselves: stop restoring. */
const USER_INPUT = [
  'touchstart',
  'wheel',
  'keydown',
  'pointerdown',
  'focusin',
] as const;

interface SavedScroll {
  path: string;
  y: number;
}

interface RestorationDiagnostics {
  active: boolean;
  target: number | null;
  corrections: number;
  before: number | null;
  after: number | null;
  stoppedBy: string | null;
  startedAt: number;
  layoutRecovery: boolean;
  compensation: number;
  deferTopCleanup: boolean;
}

let diagnostics: RestorationDiagnostics | null = null;

/** Read-only recovery state for regression checks; no UI or polling is installed. */
export const getScrollRestorationDiagnostics = () =>
  diagnostics ? { ...diagnostics } : null;

/** Native scroll compensation occupies clipped space, not usable screen height. */
export const getScrollCompensation = () => diagnostics?.compensation ?? 0;

function read(): SavedScroll | null {
  try {
    const saved = JSON.parse(
      window.sessionStorage.getItem(KEY) ?? 'null',
    ) as SavedScroll | null;
    return saved &&
      typeof saved.path === 'string' &&
      typeof saved.y === 'number' &&
      Number.isFinite(saved.y)
      ? { path: saved.path, y: Math.max(0, saved.y) }
      : null;
  } catch {
    return null;
  }
}

function write(saved: SavedScroll) {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(saved));
  } catch {
    // No storage (private mode, quota): nothing to restore next time.
  }
}

const maxScroll = () =>
  Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

/** Starts saving and, if this tab saved a position here, restores it. */
export function installScrollRestoration(): () => void {
  // Snapshot the rollout flag for this page lifetime. Reload after changing it;
  // toggling during an active native gesture could move a control under a finger.
  let viewportWorkaround = FLAG_DEFAULTS[FEATURE_FLAGS.IOS_VIEWPORT_WORKAROUND];
  try {
    viewportWorkaround =
      getFeatureFlag(FEATURE_FLAGS.IOS_VIEWPORT_WORKAROUND) !== false;
  } catch {
    // Unavailable/corrupt storage must not prevent the app from starting.
  }
  // The app owns restoration. Leaving the browser in `auto` lets it apply
  // another offset after our restore, once the document finishes loading.
  const nativeMode = window.history.scrollRestoration;
  if (viewportWorkaround && 'scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }
  const saved = read();
  // With recovery disabled, a saved zero needs no app-driven restoration.
  const savedTarget = viewportWorkaround || (saved?.y ?? 0) > 0 ? saved : null;
  const ios = isIOS();
  const braveIOS = isBraveIOS();
  const deferTopCleanup = viewportWorkaround && (braveIOS || isGoogleAppIOS());
  const navigation = performance.getEntriesByType?.('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  // Brave sets a scroll offset *after* load, even with history restoration
  // disabled: on the first visit (`navigate`) and on refresh (`reload`).
  // Compensate in the scroll event, so the page doesn't paint with the
  // header under the address bar. A saved position further down the page
  // is a real place to return to, not this inset.
  const settleTop =
    viewportWorkaround &&
    ios &&
    (navigation?.type === 'navigate' || navigation?.type === 'reload') &&
    (!savedTarget ||
      savedTarget.path !== window.location.pathname ||
      savedTarget.y === 0);
  const target = settleTop
    ? { path: window.location.pathname, y: 0 }
    : savedTarget;
  let restoring = target != null && target.path === window.location.pathname;
  const measured: RestorationDiagnostics = {
    active: restoring,
    target: target?.y ?? null,
    corrections: 0,
    before: null,
    after: null,
    stoppedBy: null,
    startedAt: performance.now(),
    layoutRecovery: false,
    compensation: 0,
    deferTopCleanup,
  };
  diagnostics = measured;
  let retry: number | undefined;
  let giveUp: number | undefined;
  let saveTimer: number | undefined;
  let onLoad: (() => void) | undefined;
  let releaseCompensation: (() => void) | undefined;
  let updateCompensation: ((offset: number) => void) | undefined;
  let settleCleanupTimer: number | undefined;
  let touching = false;
  let pointing = false;
  const interacting = () => touching || pointing;

  const releaseAtRest = () => {
    if (!interacting() && measured.compensation && Math.abs(window.scrollY) < 1)
      releaseCompensation?.();
  };
  const scheduleTopCleanup = () => {
    window.clearTimeout(settleCleanupTimer);
    if (!interacting() && measured.compensation && Math.abs(window.scrollY) < 1)
      settleCleanupTimer = window.setTimeout(releaseAtRest, 200);
  };
  const onInteractionStart = (event: Event) => {
    if (event.type === 'touchstart') touching = true;
    else pointing = true;
    window.clearTimeout(settleCleanupTimer);
  };
  const onInteractionEnd = (event: Event) => {
    if (event.type.startsWith('touch'))
      touching = Boolean((event as TouchEvent).touches?.length);
    else pointing = false;
    scheduleTopCleanup();
  };

  const compensate = () => {
    const root = document.getElementById('root');
    if (!restoring || !root || target?.path !== window.location.pathname)
      return;
    const offset = Math.max(0, window.scrollY);
    if (offset < 1) return;
    if (updateCompensation) {
      updateCompensation(offset);
      return;
    }
    const previousPadding = root.style.getPropertyValue('padding-top');
    const paddingPriority = root.style.getPropertyPriority('padding-top');
    const previousAnchor = root.style.getPropertyValue('overflow-anchor');
    const anchorPriority = root.style.getPropertyPriority('overflow-anchor');
    const originalPadding = parseFloat(getComputedStyle(root).paddingTop) || 0;
    // Keep the content in its intended visible position when the native
    // scroller will not retain zero. Exclude this insertion from anchoring.
    root.style.setProperty('overflow-anchor', 'none');
    updateCompensation = (nextOffset) => {
      if (nextOffset === measured.compensation) return;
      root.style.setProperty(
        'padding-top',
        `${originalPadding + nextOffset}px`,
      );
      measured.corrections += 1;
      measured.before = nextOffset;
      measured.after = window.scrollY;
      measured.layoutRecovery = true;
      measured.compensation = nextOffset;
    };
    updateCompensation(offset);
    const observer = new MutationObserver(() => {
      if (window.location.pathname !== target.path) releaseCompensation?.();
    });
    releaseCompensation = () => {
      window.clearTimeout(settleCleanupTimer);
      root.style.setProperty('padding-top', previousPadding, paddingPriority);
      root.style.setProperty('overflow-anchor', previousAnchor, anchorPriority);
      measured.compensation = 0;
      observer.disconnect();
      releaseCompensation = undefined;
      updateCompensation = undefined;
    };
    // The native inset can survive Home/Continue navigation.
    if (!deferTopCleanup)
      observer.observe(root, { childList: true, subtree: true });
  };

  const save = () => {
    if (restoring) return;
    write({
      path: window.location.pathname,
      y: Math.max(0, Math.round(window.scrollY - measured.compensation)),
    });
  };
  const scheduleSave = () => {
    if (measured.compensation) {
      if (!deferTopCleanup && window.location.pathname !== target?.path)
        releaseCompensation?.();
      // An iOS native pull gesture can pass through zero then return to
      // its inset. Removing padding mid-gesture makes the header disappear.
      else if (deferTopCleanup) scheduleTopCleanup();
      else if (window.scrollY <= 0) releaseCompensation?.();
    }
    // Scroll events run before the next rendering update. Correct the layout
    // in this event instead of painting a clipped header and repairing it later.
    if (settleTop && restoring) compensate();
    window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(save, SAVE_DELAY_MS);
  };
  const onHide = () => {
    if (document.visibilityState === 'hidden') save();
  };

  const stopRestoring = (event?: Event) => {
    restoring = false;
    measured.active = false;
    measured.stoppedBy = event?.type ?? 'finished';
    window.clearInterval(retry);
    window.clearTimeout(giveUp);
    if (onLoad) window.removeEventListener('load', onLoad);
    USER_INPUT.forEach((type) =>
      window.removeEventListener(type, stopRestoring),
    );
  };

  if (restoring && target) {
    const attempt = () => {
      if (window.location.pathname !== target.path) {
        stopRestoring();
        return true;
      }
      if (settleTop) {
        compensate();
        return true;
      }
      if (maxScroll() < target.y - 1) return false;
      if (Math.abs(window.scrollY - target.y) >= 1) {
        measured.corrections += 1;
        measured.before = window.scrollY;
        window.scrollTo(0, target.y);
        measured.after = window.scrollY;
      }
      stopRestoring();
      return true;
    };
    const reached = attempt();
    if (settleTop || !reached) {
      if (!settleTop) retry = window.setInterval(attempt, RETRY_MS);
      onLoad = () => {
        giveUp = window.setTimeout(() => {
          const furthest = Math.min(target.y, maxScroll());
          if (!settleTop && Math.abs(window.scrollY - furthest) >= 1)
            window.scrollTo(0, furthest);
          stopRestoring();
        }, RESTORE_WINDOW_MS);
      };
      if (settleTop && document.readyState !== 'complete') {
        window.addEventListener('load', onLoad, { once: true });
      } else {
        onLoad();
      }
      USER_INPUT.forEach((type) =>
        window.addEventListener(type, stopRestoring, { passive: true }),
      );
    }
  }

  window.addEventListener('scroll', scheduleSave, { passive: true });
  // iOS can report the viewport movement before it dispatches document scroll.
  // Correct in that earlier event while the initial at-top guard is active.
  if (settleTop && braveIOS)
    window.visualViewport?.addEventListener('scroll', compensate, { passive: true });
  window.addEventListener('pagehide', save);
  document.addEventListener('visibilitychange', onHide);
  if (deferTopCleanup) {
    window.addEventListener('pointerdown', onInteractionStart, {
      passive: true,
    });
    window.addEventListener('touchstart', onInteractionStart, {
      passive: true,
    });
    for (const type of [
      'pointerup',
      'pointercancel',
      'touchend',
      'touchcancel',
    ])
      window.addEventListener(type, onInteractionEnd, { passive: true });
    window.addEventListener('scrollend', braveIOS ? scheduleTopCleanup : releaseAtRest);
  }

  return () => {
    stopRestoring();
    releaseCompensation?.();
    window.clearTimeout(saveTimer);
    window.clearTimeout(settleCleanupTimer);
    window.removeEventListener('scroll', scheduleSave);
    if (settleTop && braveIOS)
      window.visualViewport?.removeEventListener('scroll', compensate);
    window.removeEventListener('pagehide', save);
    document.removeEventListener('visibilitychange', onHide);
    if (deferTopCleanup) {
      window.removeEventListener('pointerdown', onInteractionStart);
      window.removeEventListener('touchstart', onInteractionStart);
      for (const type of [
        'pointerup',
        'pointercancel',
        'touchend',
        'touchcancel',
      ])
        window.removeEventListener(type, onInteractionEnd);
      window.removeEventListener('scrollend', braveIOS ? scheduleTopCleanup : releaseAtRest);
    }
    if (viewportWorkaround && nativeMode != null)
      window.history.scrollRestoration = nativeMode;
  };
}
